/**
 * ExportComptablePage
 * Export des écritures comptables SYSCOHADA
 * Route : /paie/rapports/export-comptable
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BASE_ADMIN = '/api/v1/paie/administration';
const BASE_BULLETINS = '/api/v1/paie/bulletins';

interface EcritureComptable {
  journal: string;
  date: string;
  piece: string;
  compte: string;
  libelle: string;
  debit: number;
  credit: number;
}

interface ExportRecord {
  idExport: string;
  mois: number;
  annee: number;
  dateExport: string;
  nombreLignes: number;
  format: string;
}

const NOMS_MOIS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function genererCSV(ecritures: EcritureComptable[]): string {
  const header = 'Journal;Date;Pièce;Compte;Libellé;Débit;Crédit\n';
  const lignes = ecritures.map(e =>
    `${e.journal};${e.date};${e.piece};${e.compte};${e.libelle};${e.debit};${e.credit}`
  ).join('\n');
  return '\uFEFF' + header + lignes;
}

function telechargerCSV(csv: string, mois: number, annee: number) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `export-comptable-${annee}-${String(mois).padStart(2, '0')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ExportHistoriqueTable({ exports }: { exports: ExportRecord[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-raised border-b border-border">
            <th className="px-4 py-3 text-left text-xs font-semibold text-content-subtle uppercase">Période</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-content-subtle uppercase">Date export</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-content-subtle uppercase">Lignes</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-content-subtle uppercase">Format</th>
          </tr>
        </thead>
        <tbody>
          {exports.map((e, i) => (
            <tr key={e.idExport} className={i % 2 === 0 ? 'bg-surface' : 'bg-surface-raised'}>
              <td className="px-4 py-3 font-medium text-content">
                {NOMS_MOIS[e.mois - 1]} {e.annee}
              </td>
              <td className="px-4 py-3 text-content-subtle">
                {new Date(e.dateExport).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
              </td>
              <td className="px-4 py-3 text-right text-content-muted font-mono">{e.nombreLignes}</td>
              <td className="px-4 py-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-light text-accent">{e.format}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ExportComptablePage() {
  const today = new Date();
  const [mois, setMois] = useState(today.getMonth() + 1);
  const [annee, setAnnee] = useState(today.getFullYear());
  const [ecritures, setEcritures] = useState<EcritureComptable[] | null>(null);
  const [exportError, setExportError] = useState('');

  const queryClient = useQueryClient();

  const { data: exports = [] } = useQuery<ExportRecord[]>({
    queryKey: ['exports-comptables'],
    queryFn: () => axios.get(`${BASE_ADMIN}/exports`).then(r => r.data?.data ?? r.data),
  });

  const { data: org } = useQuery({
    queryKey: ['organisation-moi'],
    queryFn: () => axios.get(`${BASE_ADMIN}/organisation/moi`).then(r => r.data?.data ?? null),
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      axios.post(`${BASE_ADMIN}/exporter-comptable`, { mois, annee }).then(r => r.data?.data ?? r.data),
    onSuccess: (data: any) => {
      const result: EcritureComptable[] = data.ecritures;
      setEcritures(result);
      setExportError('');
      queryClient.invalidateQueries({ queryKey: ['exports-comptables'] });
      // Déclencher le téléchargement
      const csv = genererCSV(result);
      telechargerCSV(csv, mois, annee);
    },
    onError: (e: any) => {
      setExportError(e?.response?.data?.message || 'Erreur lors de la génération de l\'export');
      setEcritures(null);
    },
  });

  const annees = useMemo(() => {
    const list = [];
    for (let y = today.getFullYear(); y >= today.getFullYear() - 5; y--) list.push(y);
    return list;
  }, []);

  const totalDebit = ecritures?.reduce((s, e) => s + e.debit, 0) ?? 0;
  const totalCredit = ecritures?.reduce((s, e) => s + e.credit, 0) ?? 0;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* En-tête */}
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-bold text-content">Export comptable</h1>
          {org && (
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                org.referentielComptable === 'SYCEBNL'
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-primary-light text-primary-text'
              }`}
            >
              {org.referentielComptable === 'SYCEBNL'
                ? 'SYCEBNL — Entité à but non lucratif'
                : 'SYSCOHADA'}
            </span>
          )}
        </div>
        <p className="text-sm text-content-subtle mt-1">
          Génération du journal de paie (CSV téléchargeable)
        </p>
        {org?.referentielComptable === 'SYCEBNL' && (
          <p className="text-xs text-content-subtle italic mt-1">
            Les écritures de paie sont identiques — seule la référence du journal diffère (PAIE-SYCEBNL-*).
          </p>
        )}
        <Link to="/paie/configuration/organisation" className="text-xs text-primary-text hover:underline mt-1 inline-block">
          Configurer l'organisation
        </Link>
      </div>

      {/* Section export */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-6 space-y-5">
        <h2 className="text-sm font-semibold text-content-muted uppercase tracking-wide">Paramètres d'export</h2>

        <div className="flex flex-wrap gap-4 items-end">
          {/* Sélecteur mois */}
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Mois</label>
            <select
              value={mois}
              onChange={e => setMois(Number(e.target.value))}
              className="px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
            >
              {NOMS_MOIS.map((nom, i) => (
                <option key={i + 1} value={i + 1}>{nom}</option>
              ))}
            </select>
          </div>

          {/* Sélecteur année */}
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Année</label>
            <select
              value={annee}
              onChange={e => setAnnee(Number(e.target.value))}
              className="px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
            >
              {annees.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* Bouton export */}
          <button
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            className="flex items-center gap-2 px-5 py-2 bg-primary text-content-inverted text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors"
          >
            <Download className="w-4 h-4" />
            {exportMutation.isPending ? 'Génération…' : `Exporter ${NOMS_MOIS[mois - 1]} ${annee}`}
          </button>
        </div>

        {exportError && (
          <div className="flex items-center gap-2 rounded-lg bg-danger-light border border-danger p-3 text-danger text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {exportError}
          </div>
        )}
      </div>

      {/* Aperçu des écritures générées */}
      {ecritures && ecritures.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-content-muted uppercase tracking-wide">
              Aperçu — {ecritures.length} écriture(s)
            </h2>
            <button
              onClick={() => {
                const csv = genererCSV(ecritures);
                telechargerCSV(csv, mois, annee);
              }}
              className="flex items-center gap-1.5 text-xs text-primary-text hover:text-primary font-medium"
            >
              <Download className="w-3.5 h-3.5" />
              Re-télécharger CSV
            </button>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="bg-slate-800 text-slate-200">
                  <th className="px-3 py-2 text-left font-semibold">Journal</th>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Pièce</th>
                  <th className="px-3 py-2 text-left font-semibold">Compte</th>
                  <th className="px-3 py-2 text-left font-semibold">Libellé</th>
                  <th className="px-3 py-2 text-right font-semibold text-blue-300">Débit</th>
                  <th className="px-3 py-2 text-right font-semibold text-emerald-300">Crédit</th>
                </tr>
              </thead>
              <tbody>
                {ecritures.map((e, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-surface' : 'bg-surface-raised'}>
                    <td className="px-3 py-2 text-content-subtle">{e.journal}</td>
                    <td className="px-3 py-2 text-content-subtle">{e.date}</td>
                    <td className="px-3 py-2 text-content-muted">{e.piece}</td>
                    <td className="px-3 py-2 font-bold text-content">{e.compte}</td>
                    <td className="px-3 py-2 text-content-muted font-sans">{e.libelle}</td>
                    <td className="px-3 py-2 text-right text-blue-700 font-semibold">
                      {e.debit > 0 ? e.debit.toLocaleString('fr-FR') : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-700 font-semibold">
                      {e.credit > 0 ? e.credit.toLocaleString('fr-FR') : '—'}
                    </td>
                  </tr>
                ))}
                {/* Totaux */}
                <tr className="bg-surface-overlay border-t-2 border-border-strong">
                  <td colSpan={5} className="px-3 py-2 font-bold text-content font-sans text-xs">TOTAUX</td>
                  <td className="px-3 py-2 text-right font-bold text-blue-800">{totalDebit.toLocaleString('fr-FR')}</td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-800">{totalCredit.toLocaleString('fr-FR')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Historique des exports */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-content-subtle" />
          <h2 className="text-sm font-semibold text-content-muted uppercase tracking-wide">Historique des exports</h2>
        </div>

        {exports.length === 0 ? (
          <div className="text-center py-8 text-content-subtle text-sm">
            Aucun export effectué pour l'instant.
          </div>
        ) : (
          <ExportHistoriqueTable exports={exports} />
        )}
      </div>
    </div>
  );
}
